import * as THREE from 'three';
import * as SceneModule from '../scene.js';

// Sonic boom particle system
let particleSystem = null;
let particleGeometry = null;
let particleMaterial = null;
let particlePositions = null;
let particleOpacities = null;
let isActive = false;
let lifetime = 0;
const maxLifetime = 0.5; // 0.5 seconds
const particleCount = 80; // Number of particles in the ring
let spawnPosition = new THREE.Vector3();
let spawnDirection = new THREE.Vector3();
let spawnRight = new THREE.Vector3();
let spawnUp = new THREE.Vector3();

// Cooldown tracking
let lastSonicBoomTime = -15.0; // Initialize to allow first use immediately
const sonicBoomCooldown = 15.0; // 15 seconds cooldown

// Reusable vectors for calculations
const _tempVec = new THREE.Vector3();
const _tempRight = new THREE.Vector3();
const _tempUp = new THREE.Vector3();

/**
 * Initialize the sonic boom particle system
 */
function initSonicBoom() {
  if (particleSystem) return; // Already initialized

  const { scene } = SceneModule;

  // Create geometry for particles
  particleGeometry = new THREE.BufferGeometry();
  
  // Initialize position and opacity arrays
  particlePositions = new Float32Array(particleCount * 3);
  particleOpacities = new Float32Array(particleCount);
  
  // Initialize all particles at origin (will be positioned on trigger)
  for (let i = 0; i < particleCount; i++) {
    particlePositions[i * 3] = 0;
    particlePositions[i * 3 + 1] = 0;
    particlePositions[i * 3 + 2] = 0;
    particleOpacities[i] = 0;
  }
  
  particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
  
  // Create custom shader material for particles with opacity
  particleMaterial = new THREE.ShaderMaterial({
    uniforms: {
      opacity: { value: 1.0 },
      color: { value: new THREE.Color(0xffffff) }, // White/cyan for retro look
    },
    vertexShader: `
      attribute float opacity;
      varying float vOpacity;
      
      void main() {
        vOpacity = opacity;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        gl_PointSize = 8.0;
      }
    `,
    fragmentShader: `
      uniform vec3 color;
      varying float vOpacity;
      
      void main() {
        float dist = distance(gl_PointCoord, vec2(0.5));
        float alpha = 1.0 - smoothstep(0.0, 0.5, dist);
        gl_FragColor = vec4(color, alpha * vOpacity);
      }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending, // Bright shockwave look
  });
  
  // Add opacity as attribute
  particleGeometry.setAttribute('opacity', new THREE.BufferAttribute(particleOpacities, 1));
  
  particleSystem = new THREE.Points(particleGeometry, particleMaterial);
  particleSystem.visible = false;
  scene.add(particleSystem);
}

/**
 * Check if the sonic boom effect can be triggered (cooldown check)
 * @param {number} currentTime - Current time in seconds
 * @returns {boolean} True if cooldown has passed
 */
export function canTriggerSonicBoom(currentTime) {
  return (currentTime - lastSonicBoomTime) >= sonicBoomCooldown;
}

/**
 * Trigger the sonic boom effect at the given position and direction
 * @param {THREE.Vector3} position - World position of the aircraft nose
 * @param {THREE.Vector3} direction - Forward direction vector of the aircraft
 * @param {number} currentTime - Current time in seconds (for cooldown tracking)
 */
export function triggerSonicBoom(position, direction, currentTime) {
  if (!particleSystem) {
    initSonicBoom();
  }
  
  // Check cooldown
  if (!canTriggerSonicBoom(currentTime)) {
    return; // Still on cooldown, don't trigger
  }
  
  // Record trigger time
  lastSonicBoomTime = currentTime;
  
  // Store spawn position and direction
  spawnPosition.copy(position);
  spawnDirection.copy(direction).normalize();
  
  // Calculate right and up vectors for ring orientation
  // Right vector: cross product of forward and world up
  spawnRight.crossVectors(spawnDirection, new THREE.Vector3(0, 1, 0)).normalize();
  // If forward is parallel to up, use a different reference
  if (spawnRight.lengthSq() < 0.01) {
    spawnRight.set(1, 0, 0);
  }
  // Up vector: cross product of right and forward
  spawnUp.crossVectors(spawnRight, spawnDirection).normalize();
  
  // Reset all particles to spawn position
  // Arrange them in a ring perpendicular to the forward direction
  const ringRadius = 0.1; // Start small
  for (let i = 0; i < particleCount; i++) {
    const angle = (i / particleCount) * Math.PI * 2;
    const radius = ringRadius;
    
    // Calculate position in ring plane (perpendicular to forward)
    _tempRight.copy(spawnRight).multiplyScalar(Math.cos(angle) * radius);
    _tempUp.copy(spawnUp).multiplyScalar(Math.sin(angle) * radius);
    
    _tempVec.copy(spawnPosition);
    _tempVec.add(_tempRight);
    _tempVec.add(_tempUp);
    
    particlePositions[i * 3] = _tempVec.x;
    particlePositions[i * 3 + 1] = _tempVec.y;
    particlePositions[i * 3 + 2] = _tempVec.z;
    particleOpacities[i] = 1.0; // Start fully opaque
  }
  
  // Update geometry attributes
  particleGeometry.attributes.position.needsUpdate = true;
  particleGeometry.attributes.opacity.needsUpdate = true;
  
  // Reset lifetime and activate
  lifetime = 0;
  isActive = true;
  particleSystem.visible = true;
}

/**
 * Update the sonic boom particle system each frame
 * @param {number} delta - Time delta in seconds
 */
export function updateSonicBoom(delta) {
  if (!particleSystem || !isActive) return;
  
  lifetime += delta;
  
  if (lifetime >= maxLifetime) {
    // Effect complete, hide it
    isActive = false;
    particleSystem.visible = false;
    return;
  }
  
  // Calculate progress (0 to 1)
  const progress = lifetime / maxLifetime;
  
  // Expand radius over time (starts at 0.1, expands to ~2.5 units)
  const minRadius = 0.1;
  const maxRadius = 2.5;
  const currentRadius = minRadius + (maxRadius - minRadius) * progress;
  
  // Fade opacity over time (ease out)
  const opacity = 1.0 - progress;
  const easedOpacity = opacity * opacity; // Ease out quad
  
  // Update particle positions and opacities
  for (let i = 0; i < particleCount; i++) {
    const angle = (i / particleCount) * Math.PI * 2;
    
    // Calculate position in ring plane
    _tempRight.copy(spawnRight).multiplyScalar(Math.cos(angle) * currentRadius);
    _tempUp.copy(spawnUp).multiplyScalar(Math.sin(angle) * currentRadius);
    
    // Position relative to spawn point, expanding outward
    _tempVec.copy(spawnPosition);
    _tempVec.add(_tempRight);
    _tempVec.add(_tempUp);
    // Move slightly forward with the expansion
    _tempVec.addScaledVector(spawnDirection, progress * 0.3);
    
    particlePositions[i * 3] = _tempVec.x;
    particlePositions[i * 3 + 1] = _tempVec.y;
    particlePositions[i * 3 + 2] = _tempVec.z;
    particleOpacities[i] = easedOpacity;
  }
  
  // Update geometry attributes
  particleGeometry.attributes.position.needsUpdate = true;
  particleGeometry.attributes.opacity.needsUpdate = true;
}
